"use client";

import styles from "./card.module.css";
import { useUserDataContext } from "../user-data-context";
import CountrySelector from "./country-selector";
import { useEffect, useRef, useState } from "react";
import { UserProfileData } from "../types";
import { useServerAction } from "zsa-react";
import { updatePersonalInfoAction } from "../actions";
import { toast } from "react-toastify";
import { useLocale, useTranslations } from "next-intl";
import { IoIosArrowDown } from "react-icons/io";
import {
  getCountryFromPrefix,
  phonePrefixes,
} from "@/lib/phone-prefixes/prefixes";
import { CircleFlag } from "react-circle-flags";

export default function PersonalInfoCard() {
  const [isEditting, setIsEditting] = useState(false);
  const [isPhonePrefixOpen, setIsPhonePrefixOpen] = useState(false);
  const [activePrefix, setActivePrefix] = useState("");
  const { state, updateMultipleFields } = useUserDataContext();
  const { execute: updatePersonalInfoExecute } = useServerAction(
    updatePersonalInfoAction
  );
  const locale = useLocale();
  const [numberSearchQuery, setNumberSearchQuery] = useState("");
  const [formData, setFormData] = useState<Partial<UserProfileData>>({
    complete_name: state.complete_name,
    first_name: state.first_name,
    last_name: state.last_name,
    company_name: state.company_name,
    city: state.city,
    phone_number: state.phone_number,
  });
  const t = useTranslations("MyAccount");
  const toggleRef = useRef<HTMLDivElement>(null);

  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const filteredPrefixes = phonePrefixes.filter((country) =>
    (displayNames.of(country.code) ?? "")
      .toLowerCase()
      .includes(numberSearchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsPhonePrefixOpen(false);
        setNumberSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.detailsWrapper}>
        <div className={styles.infoRow}>
          <p className={styles.textRow}>
            <span className={styles.rowTitle}>{t("email")}</span>
            <span className={styles.rowDetails}>{state.email}</span>
          </p>
        </div>
        {!isEditting ? (
          <div className={styles.infoRow}>
            <p className={styles.textRow}>
              <span className={styles.rowTitle}>{t("completeName")}</span>
              <span className={styles.rowDetails}>{state.complete_name}</span>
            </p>
          </div>
        ) : (
          <div className={styles.nameInputsWrapper}>
            <input
              type="text"
              placeholder={t("firstNamePlaceholder")}
              value={formData.first_name}
              aria-label={t("ariaFirstName")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  first_name: e.target.value,
                  complete_name: e.target.value + " " + formData.last_name,
                });
              }}
              className={styles.inputField}
            />
            <input
              type="text"
              placeholder={t("lastNamePlaceholder")}
              value={formData.last_name}
              aria-label={t("ariaLastName")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  last_name: e.target.value,
                  complete_name: formData.first_name + " " + e.target.value,
                });
              }}
              className={styles.inputField}
            />
          </div>
        )}
        {!isEditting ? (
          <div className={styles.infoRow}>
            <p className={styles.textRow}>
              <span className={styles.rowTitle}>{t("companyName")}</span>
              <span className={styles.rowDetails}>{state.company_name}</span>
            </p>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder={t("companyPlaceholder")}
              value={formData.company_name}
              aria-label={t("ariaCompanyName")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  company_name: e.target.value,
                });
              }}
              className={styles.inputField}
            />
          </>
        )}
        <span className={styles.selectorRow}>
          <p>
            <span className={styles.rowTitle}>{t("country")}</span>
          </p>
          <CountrySelector />
        </span>
        {!isEditting ? (
          <div className={styles.infoRow}>
            <p className={styles.textRow}>
              <span className={styles.rowTitle}>{t("city")}</span>
              <span className={styles.rowDetails}>{state.city}</span>
            </p>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder={t("cityPlaceholder")}
              value={formData.city}
              aria-label={t("ariaCity")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  city: e.target.value,
                });
              }}
              className={styles.inputField}
            />
          </>
        )}
        {!isEditting ? (
          <div className={styles.infoRow}>
            <p className={styles.textRow}>
              <span className={styles.rowTitle}>{t("phoneNumber")}</span>
              <span className={styles.rowDetails}>
                &lrm;{state.phone_number}
              </span>
            </p>
          </div>
        ) : (
          <div ref={toggleRef} className={styles.phoneInputWrapper}>
            <button
              className={styles.prefixButton}
              aria-label={t("ariaSelectPhonePrefix")}
              onClick={(e) => {
                e.preventDefault();
                setIsPhonePrefixOpen(!isPhonePrefixOpen);
                setNumberSearchQuery("");
              }}
            >
              {activePrefix && (
                <span>
                  <CircleFlag
                    countryCode={getCountryFromPrefix(
                      activePrefix
                    ).toLocaleLowerCase()}
                    className={styles.countryIcon}
                    aria-hidden="true"
                  />
                </span>
              )}
              {activePrefix == "" ? "None" : activePrefix}
              <span>
                <IoIosArrowDown
                  className={styles.arrowIcon}
                  aria-hidden="true"
                />
              </span>
            </button>
            <input
              type="number"
              placeholder={t("phonePlaceholder")}
              value={formData.phone_number}
              aria-label={t("ariaPhoneNumber")}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  phone_number: e.target.value,
                });
              }}
              className={styles.inputField}
            />
            {isPhonePrefixOpen && (
              <div className={styles.phonePrefixesWrapper}>
                <input
                  className={styles.phoneInput}
                  type="text"
                  placeholder={t("phoneCountryPlaceholder")}
                  onChange={(e) => {
                    e.preventDefault();
                    setNumberSearchQuery(e.target.value);
                  }}
                />
                <ul>
                  {filteredPrefixes.map((activePrefix, index) => {
                    return (
                      <li key={index}>
                        <button
                          className={styles.prefixListButton}
                          onClick={(e) => {
                            e.preventDefault();
                            setActivePrefix(activePrefix.dial_code);
                            setIsPhonePrefixOpen(false);
                            setNumberSearchQuery("");
                          }}
                        >
                          <span>
                            <CircleFlag
                              countryCode={activePrefix.code.toLowerCase()}
                              className={styles.countryIcon}
                            />
                          </span>
                          <span>{activePrefix.dial_code}</span>
                          <span className={styles.phoneCountryText}>
                            {displayNames.of(activePrefix.code) ?? ""}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {!isEditting ? (
        <button
          className={styles.button}
          aria-label={t("ariaEditProfile")}
          onClick={() => {
            setIsEditting(true);
          }}
        >
          {t("edit")}
        </button>
      ) : (
        <div className={styles.actionButtons}>
          <button
            className={styles.button}
            aria-label={t("ariaCancelEditing")}
            onClick={() => {
              setFormData({
                complete_name: state.complete_name,
                first_name: state.first_name,
                last_name: state.last_name,
                company_name: state.company_name,
                city: state.city,
                phone_number: state.phone_number,
              });
              setIsEditting(false);
            }}
          >
            {t("cancel")}
          </button>
          <button
            className={styles.button}
            aria-label={t("ariaSaveChanges")}
            onClick={async () => {
              const updatedPhoneNumber = activePrefix + formData.phone_number;
              setFormData((state) => ({
                ...state,
                phone_number: updatedPhoneNumber,
              }));
              if (
                state.first_name === formData.first_name &&
                state.last_name === formData.last_name &&
                state.complete_name === formData.complete_name &&
                state.company_name === formData.company_name &&
                state.city == formData.city &&
                state.phone_number == formData.phone_number
              ) {
                setIsEditting(false);
                return;
              }
              const currentState = state;
              const updatedFormData = {
                ...formData,
                phone_number: updatedPhoneNumber,
              };
              updateMultipleFields(updatedFormData);
              setIsEditting(false);
              const [data, err] = await updatePersonalInfoExecute({
                first_name: formData.first_name ?? "",
                last_name: formData.last_name ?? "",
                complete_name: formData.complete_name ?? "",
                company_name: formData.company_name ?? "",
                city: formData.city ?? "",
                phone_number: updatedPhoneNumber ?? "",
              });
              if ((data !== null && data !== undefined) || err) {
                updateMultipleFields(currentState);
                toast(
                  data?.message === "Failed to fetch" || err
                    ? "Unable to update your data."
                    : data?.message,
                  {
                    type: "error",
                  }
                );
              }
              return;
            }}
          >
            {t("done")}
          </button>
        </div>
      )}
    </div>
  );
}

export function PersonalInfoCardLoading() {
  return (
    <div className={styles.cardWrapper}>
      <div className={styles.detailsWrapper}>
        <div className={styles.infoRow}>
          <p
            className={`${styles.textRow} ${styles.isLoading}`}
            style={{ width: "70%" }}
          ></p>
        </div>
        <div className={styles.infoRow}>
          <p
            className={`${styles.textRow} ${styles.isLoading}`}
            style={{ width: "55%" }}
          ></p>
        </div>
        <div className={styles.infoRow}>
          <p
            className={`${styles.textRow} ${styles.isLoading}`}
            style={{ width: "90%" }}
          ></p>
        </div>
        <span className={styles.selectorRow}>
          <p
            className={`${styles.textRow} ${styles.isLoading}`}
            style={{ width: "60%" }}
          ></p>
        </span>
        <div className={styles.infoRow}>
          <p
            className={`${styles.textRow} ${styles.isLoading}`}
            style={{ width: "75%" }}
          ></p>
        </div>
      </div>
      <button className={`${styles.button} ${styles.isLoading}`}></button>
    </div>
  );
}
